"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { NewProductCardInnerContent } from "@/components/NewProductCard/innerContent";
import {
  getBrandStringFromPartnerKeys,
  getTagularSubject,
  setCorrelationId,
} from "@/utils/tagular/helpers";
import { cn } from "@/lib/utils";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  FormatSubType,
  FormatType,
  NYI_NUMBER,
  NYI_STRING,
} from "@/constants/tagular/main";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import { ProductClicked } from "@/hooks/eventing/types/redventures.ecommerce.v1.ProductClicked";
import useProductEvents from "@/hooks/eventing/useProductEvents";
import { useInView } from "react-intersection-observer";
import { NewProductCardProps } from "@/components/NewProductCard/types";
import { isElementHidden } from "@/utils/isElementHidden";
import { bindProductCardClickedEvent as bindSegmentProductCardClickedEvent } from "@/lib/segment";
import useWebview from "@/webview/useWebview";
import { createCustomDimensions } from "@/utils/tagular/createDimension";
import { HoverOver } from "./components/HoverOver";
import { HoverCard } from "./components/HoverCard";

export function ClientSideNewProductCard({
  onClick,
  ...props
}: NewProductCardProps) {
  const {
    partnerKey,
    name,
    href,
    location,
    tagular_variant,
    subjects,
    index = -1,
    formatType = FormatType.ProductCard,
    formatSubtype = FormatSubType.Grid,
    skipViewEvent = false,
    skipClickEvent = false,
    queryId,
    showBadges,
    uuid,
    listId,
    searchQueryId,
    hoverEffect,
    microDegreeTag,
    hoverDataControl,
    hoverOverlay,
    resultsIndex,
  } = props;
  const { getUrl } = useWebview();
  const partnerKeyEvent = props.partnerKey || props.partnerFullName;
  const tagularProduct: ProductClicked["product"] = {
    brand: getBrandStringFromPartnerKeys(
      Array.isArray(partnerKeyEvent)
        ? partnerKeyEvent
        : [partnerKeyEvent ?? ""],
    ),
    formatSubtype,
    formatType,
    location: location || NYI_STRING,
    name: name,
    variant: tagular_variant || translateVariant(props.type),
    position: index < 0 ? NYI_NUMBER : index + 1,
    category: getTagularSubject(subjects),
    productId: uuid,
  };

  const customDimensions: ProductClicked["customDimensions"] =
    createCustomDimensions([
      ["level", props.level],
      ["pacing", props.pacing],
      ["isPartOfProgram", props.isPartOfProgram],
      ["learningType", props.type],
      ["courseCount", props.childCoursesCount],
      ["weeksToComplete", props.weeksToComplete],
      ["searchQueryId", searchQueryId],
    ]);

  const { productClicked, productViewed, correlationId } = useProductEvents({
    product: tagularProduct,
    customDimensions,
  });

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Check if event was already handled by parent component
    if ((e.nativeEvent as any).__productClickedHandled) {
      onClick?.(e);
      return;
    }

    // Only fire ProductClicked event if skipClickEvent is false
    // When skipClickEvent is true, the event is handled by a parent component
    if (!skipClickEvent) {
      setCorrelationId(correlationId);
      productClicked({ listId });
    }
    // Always call onClick callback if provided, regardless of skipClickEvent
    // This ensures navigation and other behaviors still work
    onClick?.(e);
  };

  const ref = useRef<HTMLAnchorElement>(undefined);

  const {
    ref: inViewRef,
    inView,
    entry,
  } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const setRefs = useCallback(
    (node: HTMLAnchorElement) => {
      ref.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  useEffect(() => {
    if (!inView) {
      return;
    }

    bindSegmentProductCardClickedEvent({
      domElement: ref.current as Element,
      link: href,
      label: props.name,
      objectId: props.objectId,
      queryId: props?.queryId,
      index: props.resultsIndex,
      productSource: props.productSource,
      product: props.product,
      productCategory: props.type ?? "",
      resultLevel: props.resultLevel,
      uuid: props.uuid,
      title: props.parentTitle,
      activeRunKey: props.activeRunKey,
      position: (props.index ?? 0) + 1,
    });

    if (
      !skipViewEvent &&
      entry &&
      !isElementHidden(entry.target as HTMLElement)
    ) {
      productViewed({
        listId,
      });
    }
  }, [inView, listId, searchQueryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set URL query params only if there is a query for Segment eventing
  let hrefWithQueryParams;

  if (!!queryId) {
    const url = new URL(href || "/", window.location.origin);

    const position = (index < 0 ? NYI_NUMBER : index + 1).toString();
    url.searchParams.set("index", resultsIndex || PRODUCT_INDEX_EN);
    url.searchParams.set("queryId", queryId);
    url.searchParams.set("position", position);

    hrefWithQueryParams = url.toString().replace(window.location.origin, "");
  }

  // Check if any hover overlay data is available and should be shown
  const hasHoverData =
    (hoverOverlay &&
      ((hoverOverlay.primaryDescription &&
        hoverDataControl?.showPrimaryDescription) ||
        (hoverOverlay.aboutThisProduct &&
          hoverDataControl?.showAboutThisProduct) ||
        (hoverOverlay.learningBulletItems &&
          hoverDataControl?.showLearningBulletItems))) ||
    (hoverOverlay?.skillsList !== undefined &&
      hoverOverlay?.skillsList.length > 0 &&
      hoverDataControl?.showSkillsList);

  return (
    <div
      className={cn(
        "w-full max-w-[302px] mx-auto md:mx-0 relative group",
        showBadges ? "h-[414px]" : "h-[370px]",
        props.className,
      )}
    >
      {hasHoverData && hoverEffect && hoverEffect.length && (
        <>
          {hoverEffect === "HoverOver" && (
            <HoverOver
              {...props}
              onClick={handleClick}
              hrefWithQueryParams={hrefWithQueryParams}
            />
          )}
          {hoverEffect === "HoverCard" && (
            <HoverCard
              {...props}
              onClick={handleClick}
              hrefWithQueryParams={hrefWithQueryParams}
            />
          )}
          {/* Future hover effects can be added here */}
        </>
      )}
      <a
        ref={setRefs}
        onClick={handleClick}
        href={queryId ? getUrl(hrefWithQueryParams) : getUrl(href)}
        className={cn("w-full h-full no-underline", props.className)}
      >
        <NewProductCardInnerContent {...props} />
      </a>
    </div>
  );
}
